import { skipToken } from "@reduxjs/toolkit/query";
import { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { useSession } from "users/__session/session-context";
import type { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import { isProposalNoLongerAvailableError } from "work/proposals/_views/_shared/proposal-availability-error";
import Proposal from "work/proposals/proposal";
import {
  useGetProposalCollaborationQuery,
  useReplaceClientTeamMembersMutation,
  useSaveProposalCollaborationDraftMutation,
} from "work/proposals/negotiation/proposal-collaboration-api";
import type {
  ProposalCollaborationCapabilities,
  ProposalCollaborationEnvelope,
  ProposalContentOperation,
  ProposalResolutionState,
} from "work/proposals/negotiation/proposal-collaboration-contract";
import {
  buildProposalContentOperations,
  buildProposalReviewerChanges,
  buildSaveProposalDraftCommand,
  canSendProposalContentOperations,
  filterUnauthorizedProposalContentOperations,
  filterUnauthorizedProposalReviewerChanges,
  isBarredFromInitialProposalSetup,
} from "work/proposals/negotiation/proposal-command-builder";
import {
  loadProposalCollaborationQueues,
  proposalCollaborationStorageScopeFromSession,
  proposalCollaborationViewerScope,
} from "work/proposals/negotiation/proposal-collaboration-local-store";
import {
  authoritativeProposalClientTeamRoster,
  proposalClientTeamRostersAreEqual,
  rebaseProposalClientTeamRoster,
} from "work/proposals/negotiation/proposal-client-team-roster";
import {
  proposalClientTeamMembersSaved,
  proposalEnvelopeReceived,
  proposalLocalChangesDiscarded,
  proposalLocalQueueHydrated,
  proposalOperationsQueued,
  proposalPendingContentOperationsRebased,
  proposalReviewerChangesQueued,
  proposalViewerScopeActivated,
  selectEffectiveProposalJSON,
  selectEffectiveProposalResolutionStates,
  selectProposalCollaborationEntry,
  selectProposalCollaborationViewerScope,
  selectProposalHasPendingChanges,
  selectProposalHasPendingClientTeamMembers,
  selectProposalHasPendingContentChanges,
  selectProposalLocalQueueHydrated,
  selectProposalNegotiationIdByProposalId,
  selectProposalPendingChanges,
  type ProposalPendingChanges,
} from "work/proposals/negotiation/proposal-collaboration-redux-slice";
import { applyCurrentReviewerApprovals } from "work/proposals/negotiation/proposal-reviewer-approval-state";
import {
  buildProposalResolutionDecisionOperations,
  redlineChangeTargetsResolutionState,
} from "work/proposals/negotiation/proposal-resolution-decision-builder";
import { proposalContentOperationTargetsResolutionTarget } from "work/proposals/negotiation/proposal-operation-containment";
import { isProposalResolutionTargetResolvable } from "work/proposals/negotiation/proposal-resolution-target";
import { proposalSemanticJsonValuesEqual } from "work/proposals/negotiation/proposal-collection-codec";
import ProposalSchema from "work/proposals/_schemas/proposal-schema";

/**
 * The canonical negotiation GET is authoritative in every proposal state, so there is no legacy
 * fallback: `disabled` covers an unopened (id-less) editor, `loading`/`error` cover the canonical
 * fetch lifecycle, and `canonical` is the live projection. A load failure surfaces as `error`,
 * never a second editable snapshot.
 */
export type PersistedProposalCollaborationMode = "disabled" | "loading" | "canonical" | "error";

type QueueProposalOptions = {
  /** Active proposals edit a redline; their current values must be flattened before creating operations. */
  flattenRedline?: boolean;
  /**
   * The caller is only reconciling the editor against the queue to catch a final un-queued edit
   * (the Save handler's re-flatten), never expressing a removal. Drop a diff-derived
   * `CollectionItemRemoved` that merely cancels a still-pending or in-flight `CollectionItemAdded`,
   * so an editor snapshot that lagged an async collection add cannot compose the genuine add to
   * null. Genuine removals never need this: they flow through the ordinary edit path, which composes
   * the pending add away at the moment of the removal.
   */
  preservePendingCollectionAdds?: boolean;
};

const emptyProposalPendingChanges: ProposalPendingChanges = { operations: [], reviewerChanges: [] };

export type PersistedProposalCollaboration = {
  mode: PersistedProposalCollaborationMode;
  viewerScope?: string;
  envelope?: ProposalCollaborationEnvelope;
  effectiveProposalJSON?: ProposalJSON;
  resolutionStates: ProposalResolutionState[];
  capabilities?: ProposalCollaborationCapabilities;
  /**
   * Whether the page is holding anything the next Save would write — the staged client-team roster
   * included. The page's Save button, its discard-on-navigate prompt and Save & Close all ask this.
   */
  hasPendingChanges: boolean;
  /**
   * Whether the page is holding unsaved *negotiated content* — content operations and reviewer
   * changes, and nothing else.
   *
   * What submission eligibility is measured on. The roster is deliberately absent: it is written by
   * the same Save press but it is reference state, so a roster-only edit is unsaved work with
   * nothing in it to send the counterparty (#5276).
   */
  hasPendingContentChanges: boolean;
  /**
   * Whether the next Save also has a client-team roster to write.
   *
   * Told apart from the content queue because the two are gated differently: managing the roster
   * is its own capability, so an actor who may not edit the draft can still have a roster waiting
   * for this Save (#5116).
   */
  hasPendingClientTeamMembers: boolean;
  /** Exactly what the next Save will persist — what the Save confirmation describes to the user. */
  pendingChanges: ProposalPendingChanges;
  isLoading: boolean;
  isFetching: boolean;
  isSaving: boolean;
  error?: unknown;
  queueProposalUpdate: (updatedProposal: Proposal, options?: QueueProposalOptions) => boolean;
  savePendingChanges: () => Promise<ProposalCollaborationEnvelope | undefined>;
  discardPendingChanges: () => void;
  refetch: () => Promise<unknown>;
};

/**
 * Canonical editor for persisted proposals.
 *
 * The canonical negotiation GET is authoritative in every proposal state (unsaved draft,
 * saved draft, submitted, active) — a null stream is served with synthesized cursors — so
 * there is no legacy fallback: a load failure surfaces as `error`, never a second editable
 * snapshot. Once an envelope has loaded, the page continues to render this projection
 * (including its durable local queue) even if a later refresh is temporarily offline.
 */
export function usePersistedProposalCollaboration(proposalId?: string): PersistedProposalCollaboration {
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();
  const storageScope = useMemo(() => proposalCollaborationStorageScopeFromSession(session), [session]);
  const viewerScope = useMemo(
    () => (storageScope ? proposalCollaborationViewerScope(storageScope) : undefined),
    [storageScope],
  );
  const query = useGetProposalCollaborationQuery(proposalId && viewerScope ? { proposalId, viewerScope } : skipToken, {
    refetchOnMountOrArgChange: true,
  });
  const [saveDraft, saveResult] = useSaveProposalCollaborationDraftMutation();
  // The client-team roster has its own command because membership is reference state rather than
  // negotiated content, but it is committed by the same explicit Save (#5116) — so the hook that
  // owns Save owns this too, instead of the tab writing behind the page's back.
  const [replaceClientTeamMembers, replaceClientTeamMembersResult] = useReplaceClientTeamMembersMutation();

  // RTK Query retains the last successful envelope for this subscription, including
  // across a failed refetch, so the negotiation identity survives a temporary outage
  // without a render-time cache of its own.
  const mappedNegotiationIdSelector = useMemo(
    () => selectProposalNegotiationIdByProposalId(proposalId ?? ""),
    [proposalId],
  );
  const mappedNegotiationId = useSelector(mappedNegotiationIdSelector);
  const activeViewerScope = useSelector(selectProposalCollaborationViewerScope);
  const scopeIsActive = Boolean(viewerScope && activeViewerScope === viewerScope);
  const negotiationId = query.currentData?.negotiationId ?? (scopeIsActive ? mappedNegotiationId : undefined);

  const entrySelector = useMemo(() => selectProposalCollaborationEntry(negotiationId ?? ""), [negotiationId]);
  const effectiveSelector = useMemo(() => selectEffectiveProposalJSON(negotiationId ?? ""), [negotiationId]);
  const effectiveResolutionSelector = useMemo(
    () => selectEffectiveProposalResolutionStates(negotiationId ?? ""),
    [negotiationId],
  );
  const pendingSelector = useMemo(() => selectProposalHasPendingChanges(negotiationId ?? ""), [negotiationId]);
  const pendingClientTeamSelector = useMemo(
    () => selectProposalHasPendingClientTeamMembers(negotiationId ?? ""),
    [negotiationId],
  );
  const pendingContentSelector = useMemo(
    () => selectProposalHasPendingContentChanges(negotiationId ?? ""),
    [negotiationId],
  );
  const pendingChangesSelector = useMemo(() => selectProposalPendingChanges(negotiationId ?? ""), [negotiationId]);
  const hydratedSelector = useMemo(
    () => selectProposalLocalQueueHydrated(negotiationId ?? "", viewerScope ?? ""),
    [negotiationId, viewerScope],
  );
  const entry = useSelector(entrySelector);
  const selectedEffectiveProposalJSON = useSelector(effectiveSelector);
  const selectedEffectiveResolutionStates = useSelector(effectiveResolutionSelector);
  const hasPendingChanges = useSelector(pendingSelector);
  const hasPendingClientTeamMembers = useSelector(pendingClientTeamSelector);
  const hasPendingContentChanges = useSelector(pendingContentSelector);
  const pendingChanges = useSelector(pendingChangesSelector);
  const localQueueHydrated = useSelector(hydratedSelector);

  useEffect(() => {
    if (viewerScope) dispatch(proposalViewerScopeActivated({ viewerScope }));
  }, [dispatch, viewerScope]);

  // The endpoint lifecycle normally stores this result first. Keeping the hook
  // idempotently responsible as well makes account switches deterministic even
  // when an old request finishes after the session context changed.
  useEffect(() => {
    if (viewerScope && query.currentData) {
      dispatch(proposalEnvelopeReceived({ envelope: query.currentData, viewerScope }));
    }
  }, [dispatch, query.currentData, viewerScope]);

  // A negotiation id only exists once some envelope has been observed, so the authorization answer is
  // available at the moment the durable queue is rehydrated.
  const hydrationCapabilities = entry?.envelope?.capabilities ?? query.currentData?.capabilities;

  useEffect(() => {
    if (!storageScope || !viewerScope || !negotiationId || localQueueHydrated) return;
    const storedQueue = loadProposalCollaborationQueues(undefined, storageScope)[negotiationId];
    dispatch(
      proposalLocalQueueHydrated({
        negotiationId,
        viewerScope,
        // A persisted operation this actor may never send would otherwise sit here forever: every
        // save filters it back out, yet nothing removes it, so the queue reports unsaved changes
        // that can never be saved. Dropping it at rehydration lets the durable queue drain, and the
        // persistence middleware writes the drained queue back to storage.
        pendingOperations: permanentlyUnsendableEvictedOperations(
          storedQueue?.pendingOperations ?? [],
          hydrationCapabilities,
        ),
        // The durable queue previously never inspected reviewer changes at all. Same rule as the
        // operations above: `canManageReviewers` is required on the wire, so a definite `false` evicts
        // rather than stranding a roster change no save can ever carry.
        pendingReviewerChanges: filterUnauthorizedProposalReviewerChanges(
          storedQueue?.pendingReviewerChanges ?? [],
          hydrationCapabilities,
        ),
        // In-flight commands are deliberately untouched. They were already filtered when built, and
        // an unacknowledged command must be retried byte-identical so the server can resolve it
        // idempotently — rewriting one here could double-apply a command that did commit.
        inFlightCommands: storedQueue?.inFlightCommands ?? {},
      }),
    );
  }, [dispatch, hydrationCapabilities, localQueueHydrated, negotiationId, storageScope, viewerScope]);

  const authoritativeEnvelope = (scopeIsActive ? entry?.envelope : undefined) ?? query.currentData;
  const authoritativeEffectiveProposalJSON =
    (scopeIsActive ? selectedEffectiveProposalJSON : undefined) ?? authoritativeEnvelope?.projection;
  const effectiveResolutionStates = useMemo(
    () =>
      scopeIsActive && entry
        ? selectedEffectiveResolutionStates
        : authoritativeEnvelope
          ? authoritativeEnvelope.resolutionStates
          : [],
    [authoritativeEnvelope, entry, scopeIsActive, selectedEffectiveResolutionStates],
  );
  const envelope = useMemo(
    () =>
      authoritativeEnvelope
        ? {
            ...authoritativeEnvelope,
            projection: applyCurrentReviewerApprovals(authoritativeEnvelope.projection, authoritativeEnvelope),
            resolutionStates: effectiveResolutionStates,
          }
        : undefined,
    [authoritativeEnvelope, effectiveResolutionStates],
  );
  const effectiveProposalJSON = useMemo(
    () =>
      authoritativeEnvelope && authoritativeEffectiveProposalJSON
        ? applyCurrentReviewerApprovals(authoritativeEffectiveProposalJSON, authoritativeEnvelope)
        : authoritativeEffectiveProposalJSON,
    [authoritativeEffectiveProposalJSON, authoritativeEnvelope],
  );
  const hasCanonicalProjection = Boolean(envelope && effectiveProposalJSON);
  const hasTerminalLoadError = query.isError && isProposalNoLongerAvailableError(query.error);
  // The canonical negotiation GET returns a 200 envelope in every proposal state, so there is no
  // legitimate 404 to fall back from. Any load failure — 404 included — is a surfaced error, not a
  // legacy fallback that could serve stale, pre-canonical state. RTK Query retains the last successful
  // envelope after a failed refetch, so a terminal 403/404/410 must also replace that stale projection;
  // transient refresh failures continue serving it so a network blip does not eject an active editor.
  const hasLoadError = query.isError && (!hasCanonicalProjection || hasTerminalLoadError);
  const mode: PersistedProposalCollaborationMode = !proposalId
    ? "disabled"
    : hasLoadError
      ? "error"
      : hasCanonicalProjection
        ? "canonical"
        : "loading";

  // Reading through a thunk makes callbacks observe the exact queue at event
  // time. This matters when an input blur queues an edit immediately before a
  // save click, and when another edit arrives while a command is in flight.
  const readState = useCallback(() => dispatch((_innerDispatch, getState) => getState()), [dispatch]);

  const queueProposalUpdate = useCallback(
    (updatedProposal: Proposal, options?: QueueProposalOptions): boolean => {
      if (!negotiationId || !viewerScope) return false;
      const state = readState();
      if (selectProposalCollaborationViewerScope(state) !== viewerScope) return false;
      const currentEntry = selectProposalCollaborationEntry(negotiationId)(state);
      const currentEnvelope = currentEntry?.envelope;
      const original = selectEffectiveProposalJSON(negotiationId)(state);
      if (!currentEnvelope || !original) return false;

      const existingLocalOperations = [
        ...Object.values(currentEntry.inFlightCommands).flatMap((command) => command?.operations ?? []),
        ...currentEntry.pendingOperations,
      ];

      const updated = options?.flattenRedline
        ? proposalCollaborationSnapshotFromRedline(updatedProposal)
        : updatedProposal.toJSON();
      const existingPendingContentConflictKeys = new Set(
        currentEntry.pendingOperations
          .filter((operation) => operation.kind !== "ResolutionDecision")
          .map((operation) => operation.conflictKey),
      );
      const authoritativeContentOperations = buildProposalContentOperations(
        currentEnvelope.projection,
        updated,
        undefined,
        currentEnvelope.capabilities,
      );
      const preservedAlternativeOperations = authoritativeContentOperations.filter((operation) =>
        existingPendingContentConflictKeys.has(operation.conflictKey),
      );
      const currentResolutionStates = selectEffectiveProposalResolutionStates(negotiationId)(state);
      // Gating on the envelope's capabilities rather than proposal status keeps operations the actor
      // may never send out of the durable queue entirely. `buildSaveProposalDraftCommand` still
      // filters at send time, but a queue that never accepts such an operation is what actually lets
      // the queue drain instead of carrying a permanently unsendable entry.
      const rawContentOperations = buildProposalContentOperations(
        original,
        updated,
        undefined,
        currentEnvelope.capabilities,
      );
      const redlineSessionContentOperations = options?.flattenRedline
        ? filterFlattenedRedlineSessionContentOperations(
            rawContentOperations,
            updatedProposal,
            existingLocalOperations,
            currentResolutionStates,
            currentEnvelope.viewingParty,
            currentEnvelope.currentSubmissionId,
          )
        : rawContentOperations;
      const reconciledContentOperations = filterImplicitUnresolvedTargetReverts(
        redlineSessionContentOperations,
        currentResolutionStates,
        currentEnvelope.viewingParty,
        currentEnvelope.currentSubmissionId,
        updatedProposal,
      );
      // A reconciliation re-diff — the Save handler re-flattens the editor "to catch a final
      // un-queued edit" — can run against a React-derived snapshot that lags the Redux queue after
      // an async collection add (adding a conflicts document is the canonical case; a fee category
      // is another). The queued CollectionItemAdded is already in the effective baseline, so diffing
      // that baseline against the stale snapshot misreads the queued item as removed and
      // `composeContentOperations` composes the genuine add to null. The flatten path drops this
      // artifact through its redline session-history discriminator; the builder/draft editor has no
      // redline, so a caller that is only reconciling sets `preservePendingCollectionAdds` to drop a
      // removal that merely cancels a still-pending or in-flight add.
      const reconciledOrPreservedContentOperations = options?.preservePendingCollectionAdds
        ? dropStalePendingCollectionAddReverts(reconciledContentOperations, existingLocalOperations)
        : reconciledContentOperations;
      // The last capability gate before anything durable is written. `buildProposalContentOperations`
      // above narrows the *paths* an actor may write; this narrows the *set*, which is what a
      // comment-only reviewer needs: without `canEditDraft` the server rejects a save carrying any
      // operation at all, so queuing one only produces a permanently unsendable entry that reports
      // unsaved changes forever and 403s every save and review completion (#4535).
      const contentOperations = filterUnauthorizedProposalContentOperations(
        reconciledOrPreservedContentOperations,
        currentEnvelope.capabilities,
      );
      const reviewerChanges = filterUnauthorizedProposalReviewerChanges(
        buildProposalReviewerChanges(original, updated, currentEnvelope.viewingParty),
        currentEnvelope.capabilities,
      );

      // Batch every operation this call produces into as few dispatches as possible — each dispatch is
      // one persistence-middleware pass, which does a full parse + Zod re-validate + stringify of the
      // whole durable queue. A K-operation blur/flatten used to cost K read-modify-writes (one dispatch
      // per operation); it now costs one. This is safe to batch ahead of the resolution-decision build
      // below: `currentResolutionStates` (read once, above) is exactly what a post-content-dispatch
      // read would also produce, because content operations are never `ResolutionDecision`-kind and so
      // never affect projected resolution disposition — only queued `ResolutionDecision` operations
      // (already captured in `existingLocalOperations`) do.
      if (contentOperations.length > 0) {
        dispatch(proposalOperationsQueued({ negotiationId, operations: contentOperations }));
      }
      if (reviewerChanges.length > 0) {
        dispatch(proposalReviewerChangesQueued({ negotiationId, reviewerChanges }));
      }

      // Filtered on the same capability as the content operations above: the server counts a
      // `ResolutionDecision` as an operation like any other, so an actor without `canEditDraft` may not
      // send one either (#4535).
      const resolutionOperations =
        options?.flattenRedline && canSendProposalContentOperations(currentEnvelope.capabilities)
          ? buildProposalResolutionDecisionOperations({
              proposal: updatedProposal,
              resolutionStates: currentResolutionStates,
              viewingParty: currentEnvelope.viewingParty,
              currentSubmissionId: currentEnvelope.currentSubmissionId,
              contentOperations: [...contentOperations, ...preservedAlternativeOperations],
              existingLocalOperations: [
                ...existingLocalOperations.filter((operation) => operation.kind === "ResolutionDecision"),
                ...preservedAlternativeOperations,
              ],
            })
          : [];
      const alternativeResolutionOperations = resolutionOperations.filter(
        (operation) => operation.value === "ProposeAlternative",
      );
      const rebasedContentOperations = preservedAlternativeOperations.filter((operation) =>
        alternativeResolutionOperations.some((target) =>
          proposalContentOperationTargetsResolutionTarget(operation, target),
        ),
      );
      if (rebasedContentOperations.length > 0) {
        dispatch(
          proposalPendingContentOperationsRebased({
            negotiationId,
            conflictKeys: alternativeResolutionOperations.map((operation) => operation.conflictKey),
            operations: rebasedContentOperations,
          }),
        );
      }
      if (resolutionOperations.length > 0) {
        dispatch(proposalOperationsQueued({ negotiationId, operations: resolutionOperations }));
      }

      return contentOperations.length > 0 || resolutionOperations.length > 0 || reviewerChanges.length > 0;
    },
    [dispatch, negotiationId, readState, viewerScope],
  );

  const savePendingChanges = useCallback(async (): Promise<ProposalCollaborationEnvelope | undefined> => {
    if (!negotiationId || !viewerScope) return undefined;

    const initialState = readState();
    if (selectProposalCollaborationViewerScope(initialState) !== viewerScope) return undefined;
    let currentEntry = selectProposalCollaborationEntry(negotiationId)(initialState);
    if (!currentEntry?.envelope) return undefined;

    // An uncertain response may represent a committed command whose response
    // was lost. Refetch first, then retry any still-unacknowledged command with
    // its original id so the server can resolve it idempotently.
    if (currentEntry.syncStatus === "resyncRequired") {
      const refetchedEnvelope = await query.refetch().unwrap();
      dispatch(proposalEnvelopeReceived({ envelope: refetchedEnvelope, viewerScope }));
      currentEntry = selectProposalCollaborationEntry(negotiationId)(readState());
      for (const command of Object.values(currentEntry?.inFlightCommands ?? {})) {
        if (command) {
          const retriedEnvelope = await saveDraft({ negotiationId, command, viewerScope }).unwrap();
          dispatch(proposalEnvelopeReceived({ envelope: retriedEnvelope, viewerScope }));
        }
      }
      currentEntry = selectProposalCollaborationEntry(negotiationId)(readState());
    }

    if (!currentEntry?.envelope) return undefined;

    // The roster goes first and on its own. The API rejects any command whose
    // `expectedStreamVersion` is not the stream's current one, and the fresh cursor only arrives
    // with the envelope a write returns, so two commands sent together would have the second
    // answered with a stale-cursor 409. The draft is settled only once the write has been
    // acknowledged: a failure leaves the viewer's roster staged and the error is the page's to
    // report, exactly as a failed content save is.
    const clientTeamDraft = currentEntry.pendingClientTeamMembers;
    if (clientTeamDraft) {
      const authoritativeRoster = authoritativeProposalClientTeamRoster(
        currentEntry.envelope.projection.clientTeam,
      );
      const rosterToWrite = rebaseProposalClientTeamRoster(clientTeamDraft, authoritativeRoster);
      if (!proposalClientTeamRostersAreEqual(rosterToWrite, authoritativeRoster)) {
        const rosterEnvelope = await replaceClientTeamMembers({
          negotiationId,
          viewerScope,
          command: {
            commandId: crypto.randomUUID(),
            expectedStreamVersion: currentEntry.envelope.streamVersion,
            members: rosterToWrite.map((member) => ({
              userId: member.userId,
              legalEntityId: member.legalEntityId,
            })),
          },
        }).unwrap();
        dispatch(proposalEnvelopeReceived({ envelope: rosterEnvelope, viewerScope }));
      }
      // Settle the roster that was actually written, not "whatever is staged now". The roster
      // controls stay usable for the length of that await — disabling them would leave the tab
      // looking stuck and would still drop a keystroke already under way — so the viewer can have
      // staged a further add or removal since the command left. Clearing the draft outright would
      // discard it silently; advancing it past what was written keeps it as unsaved work and
      // leaves the page dirty over it, the same way the content queue retires only the operations
      // the server acknowledged (#5276).
      dispatch(proposalClientTeamMembersSaved({ negotiationId, members: rosterToWrite }));
      currentEntry = selectProposalCollaborationEntry(negotiationId)(readState());
      if (!currentEntry?.envelope) return undefined;
    }

    if (currentEntry.pendingOperations.length === 0 && currentEntry.pendingReviewerChanges.length === 0) {
      return currentEntry.envelope;
    }

    const command = buildSaveProposalDraftCommand({
      envelope: currentEntry.envelope,
      operations: currentEntry.pendingOperations,
      reviewerChanges: currentEntry.pendingReviewerChanges,
    });
    // Emptiness is decided on the built command, not on the queue, because the wire boundary may have
    // dropped everything the queue was holding — a comment-only reviewer whose edit rights were revoked
    // while work sat in the durable queue (#4535). A save command must carry at least one content or
    // reviewer change to be valid, and the server would reject exactly what was filtered, so there is
    // nothing to send: hand back the envelope the caller asked for rather than a request that can only
    // fail. Callers that merely need the freshest cursors — review completion is the one that matters
    // here — then proceed normally.
    if (command.operations.length === 0 && command.reviewerChanges.length === 0) {
      return currentEntry.envelope;
    }

    const savedEnvelope = await saveDraft({ negotiationId, command, viewerScope }).unwrap();
    // The endpoint lifecycle also stores the response, but its `queryFulfilled`
    // continuation is not an ordering contract for callers awaiting `unwrap()`.
    // Store it here before returning so a Save-then-Submit sequence can
    // synchronously read the acknowledged cursors from Redux.
    dispatch(proposalEnvelopeReceived({ envelope: savedEnvelope, viewerScope }));
    return savedEnvelope;
  }, [dispatch, negotiationId, query, readState, replaceClientTeamMembers, saveDraft, viewerScope]);

  const discardPendingChanges = useCallback(() => {
    if (negotiationId && viewerScope && selectProposalCollaborationViewerScope(readState()) === viewerScope) {
      dispatch(proposalLocalChangesDiscarded({ negotiationId }));
    }
  }, [dispatch, negotiationId, readState, viewerScope]);

  const refetch = useCallback(async () => query.refetch().unwrap(), [query]);

  return {
    mode,
    viewerScope,
    envelope,
    effectiveProposalJSON,
    resolutionStates: effectiveResolutionStates,
    capabilities: envelope?.capabilities,
    hasPendingChanges: scopeIsActive ? hasPendingChanges : false,
    hasPendingContentChanges: scopeIsActive ? hasPendingContentChanges : false,
    hasPendingClientTeamMembers: scopeIsActive ? hasPendingClientTeamMembers : false,
    pendingChanges: scopeIsActive ? pendingChanges : emptyProposalPendingChanges,
    isLoading: mode === "loading",
    isFetching: query.isFetching,
    isSaving:
      saveResult.isLoading ||
      replaceClientTeamMembersResult.isLoading ||
      (scopeIsActive && entry?.syncStatus === "saving"),
    error: hasLoadError ? query.error : undefined,
    queueProposalUpdate,
    savePendingChanges,
    discardPendingChanges,
    refetch,
  };
}

/**
 * Returns the queued operations that survive eviction, dropping only those the current actor is
 * permanently forbidden from sending.
 *
 * Authorization is the only admissible reason to destroy queued work. The decision is taken from the
 * envelope's capabilities alone — never from a save response — so a transport failure, an offline
 * reload, or a `409` (which rebases from its own conflict envelope) can never reach this path.
 *
 * Eviction additionally requires an explicit `false` — `isBarredFromInitialProposalSetup` — rather
 * than delegating the whole decision to `filterUnauthorizedProposalContentOperations`. That function
 * treats a missing `canConfigureInitialProposalSetup` as "not authorized", which is correct when
 * deciding what to put on the wire, but during a rolling deployment an older server may instead send
 * the legacy `canSelectInitialTeams` flag, or omit both flags. Reading that silence as "unauthorized"
 * would permanently delete work the actor is in fact allowed to save. Once the current capability or
 * its legacy fallback is a definite `false`, the send-path filter supplies the forbidden paths so the
 * two can never drift.
 */
function permanentlyUnsendableEvictedOperations(
  operations: ProposalContentOperation[],
  capabilities: ProposalCollaborationCapabilities | undefined,
): ProposalContentOperation[] {
  // `canEditDraft` is a required field of the capabilities contract, so unlike the two setup flags
  // below there is no silence to interpret: a `false` is the server's definite answer that this actor
  // may send no operation of any kind. A reviewer whose edit rights were revoked while work sat in the
  // durable queue would otherwise carry it forever, 403ing every save and every review completion
  // (#4535).
  if (capabilities?.canEditDraft === false) return [];
  if (!isBarredFromInitialProposalSetup(capabilities)) return operations;
  return filterUnauthorizedProposalContentOperations(operations, capabilities);
}

/**
 * The conflict keys of every still-pending or in-flight `CollectionItemAdded` in the local queue,
 * lower-cased. Shared by both stale-pending-add-revert guards below so they can never diverge on
 * what counts as "still pending."
 */
function pendingAddedCollectionConflictKeys(
  existingLocalOperations: readonly ProposalContentOperation[],
): ReadonlySet<string> {
  return new Set(
    existingLocalOperations
      .filter((operation) => operation.kind === "CollectionItemAdded")
      .map((operation) => operation.conflictKey.toLowerCase()),
  );
}

/**
 * True when `operation` is a `CollectionItemRemoved` that cancels a still-pending or in-flight
 * `CollectionItemAdded` for the same collection item, AND no entry in `sessionHistory` records the
 * user actually acting on that item.
 *
 * Such a removal, absent a matching session-history action, is a staleness artifact of a
 * reconciliation re-diff (the Save handler re-flattening the editor) whose snapshot lagged the queue
 * after an async collection add — never a real user removal. The redline flatten path passes its
 * actual `sessionHistory` so a genuine undo/remove — which does leave a matching session action — is
 * exempted and kept; the builder/draft reconcile path has no redline to consult, so it always passes
 * an empty history, which is exactly equivalent to never exempting anything: every reconcile-path
 * match is by construction a staleness artifact, because a genuine removal there already composed the
 * pending add away through the ordinary edit path before any reconciliation runs.
 */
function isStalePendingCollectionAddRevert(
  operation: ProposalContentOperation,
  pendingAddedConflictKeys: ReadonlySet<string>,
  sessionHistory: readonly NonNullable<Proposal["redline"]>["sessionHistory"][number][] = [],
): boolean {
  return (
    operation.kind === "CollectionItemRemoved" &&
    pendingAddedConflictKeys.has(operation.conflictKey.toLowerCase()) &&
    !sessionHistory.some((change) => redlineChangeTargetsContentOperation(change, operation))
  );
}

/**
 * Drops a diff-derived `CollectionItemRemoved` whose only effect is to cancel a still-pending or
 * in-flight `CollectionItemAdded` for the same collection item.
 *
 * Used by the builder/draft reconcile path (`preservePendingCollectionAdds`), which has no redline
 * session history to consult: a genuine removal there is issued through the ordinary edit path, which
 * composes the pending add away when the user removes the item, so by the time any later
 * reconciliation runs there is no matching pending add left for this filter to protect. A removal
 * that targets a genuinely present (already-saved) item carries no matching pending add and is left
 * untouched. Shares its predicate with the redline flatten path's own guard, below, via
 * `isStalePendingCollectionAddRevert`.
 */
function dropStalePendingCollectionAddReverts(
  operations: ProposalContentOperation[],
  existingLocalOperations: readonly ProposalContentOperation[],
): ProposalContentOperation[] {
  const pendingAddedConflictKeys = pendingAddedCollectionConflictKeys(existingLocalOperations);
  if (pendingAddedConflictKeys.size === 0) return operations;
  return operations.filter((operation) => !isStalePendingCollectionAddRevert(operation, pendingAddedConflictKeys));
}

function filterFlattenedRedlineSessionContentOperations(
  operations: ProposalContentOperation[],
  updatedProposal: Proposal,
  existingLocalOperations: readonly ProposalContentOperation[],
  resolutionStates: readonly ProposalResolutionState[],
  viewingParty: ProposalCollaborationEnvelope["viewingParty"],
  currentSubmissionId: string,
): ProposalContentOperation[] {
  if (operations.length === 0) return operations;

  const sessionHistory = updatedProposal.redline?.sessionHistory ?? [];
  const incomingUnresolvedTargets = resolutionStates.filter(
    (state) =>
      state.submissionId === currentSubmissionId &&
      isProposalResolutionTargetResolvable(state, viewingParty) &&
      state.disposition === "Unresolved",
  );
  const existingLocalContentConflictKeys = new Set(
    existingLocalOperations
      .filter((operation) => operation.kind !== "ResolutionDecision")
      .map((operation) => operation.conflictKey.toLowerCase()),
  );

  const pendingAddedConflictKeys = pendingAddedCollectionConflictKeys(existingLocalOperations);

  return operations.filter((operation) => {
    if (operation.kind === "ResolutionDecision") return true;
    // A flattened removal that cancels a still-pending local add is normally a
    // staleness artifact: the editor snapshot lagged the queue, which already
    // carries the add, so diffing the effective baseline against the stale
    // snapshot misreads the queued item as removed. Such a stale re-flatten
    // records no redline session action for the item, so drop it rather than let
    // it compose the genuine add away. A genuine undo/remove of the pending add,
    // by contrast, leaves a session-history action targeting the item — keep that
    // so the undo actually cancels the add instead of silently doing nothing.
    // (Shared with the builder/draft reconcile path's own guard, above.)
    if (isStalePendingCollectionAddRevert(operation, pendingAddedConflictKeys, sessionHistory)) {
      return false;
    }
    if (existingLocalContentConflictKeys.has(operation.conflictKey.toLowerCase())) return true;
    if (incomingUnresolvedTargets.some((state) => contentOperationIsChildEditOfWholeItemTarget(operation, state))) {
      return true;
    }
    if (sessionHistory.some((change) => redlineChangeTargetsContentOperation(change, operation))) return true;
    // A genuine new collection item add — for example a new conflicts document —
    // carries no text diff and so records no redline session-history change, yet it
    // is real local work, not a flattening artifact of an unresolved incoming item.
    // Keep such an add when it touches no incoming resolution target; scalar drift
    // without a session action stays filtered.
    return (
      operation.kind === "CollectionItemAdded" &&
      !resolutionStates.some((state) => proposalContentOperationTargetsResolutionTarget(operation, state))
    );
  });
}

function contentOperationIsChildEditOfWholeItemTarget(
  operation: Exclude<ProposalContentOperation, { kind: "ResolutionDecision" }>,
  state: ProposalResolutionState,
): boolean {
  if (!state.itemId) return false;
  if (!proposalContentOperationTargetsResolutionTarget(operation, state)) return false;
  return operation.conflictKey.toLowerCase().startsWith(`${state.conflictKey.toLowerCase()}:`);
}

function redlineChangeTargetsContentOperation(
  change: NonNullable<Proposal["redline"]>["sessionHistory"][number],
  operation: Exclude<ProposalContentOperation, { kind: "ResolutionDecision" }>,
): boolean {
  return redlineChangeTargetsResolutionState(change, {
    submissionId: "",
    path: operation.path,
    conflictKey: operation.conflictKey,
    itemId: "itemId" in operation ? operation.itemId : null,
    incomingParty: "Client",
    incomingSequence: 0,
    disposition: "Unresolved",
    baselineValueExists: false,
    targetValueExists: false,
    actorUserId: null,
    actorLegalEntityId: null,
    actorRole: null,
    decisionSequence: null,
    decisionDate: null,
  });
}

function filterImplicitUnresolvedTargetReverts(
  operations: ProposalContentOperation[],
  resolutionStates: readonly ProposalResolutionState[],
  viewingParty: ProposalCollaborationEnvelope["viewingParty"],
  currentSubmissionId: string,
  updatedProposal: Proposal,
): ProposalContentOperation[] {
  if (operations.length === 0) return operations;

  const incomingTargets = resolutionStates.filter(
    (state) =>
      state.submissionId === currentSubmissionId &&
      isProposalResolutionTargetResolvable(state, viewingParty) &&
      state.disposition === "Unresolved",
  );
  if (incomingTargets.length === 0) return operations;

  const sessionHistory = updatedProposal.redline?.sessionHistory ?? [];
  return operations.filter((operation) => {
    if (operation.kind === "ResolutionDecision") return true;
    return !incomingTargets.some((state) =>
      shouldIgnoreImplicitUnresolvedTargetRevert(operation, state, sessionHistory),
    );
  });
}

function shouldIgnoreImplicitUnresolvedTargetRevert(
  operation: Exclude<ProposalContentOperation, { kind: "ResolutionDecision" }>,
  state: ProposalResolutionState,
  sessionHistory: NonNullable<Proposal["redline"]>["sessionHistory"],
): boolean {
  if (!proposalContentOperationTargetsResolutionTarget(operation, state)) return false;
  if (sessionHistory.some((change) => redlineChangeTargetsResolutionState(change, state))) return false;

  // Flattening an active redline is intentionally a save representation, not
  // an implicit resolution gesture. For an untouched incoming collection add,
  // the redline has no current entry, so the flattened snapshot can look like a
  // whole-item removal. Preserve the target until the user explicitly accepts,
  // rejects, or edits that exact item. Child edits still remain alternatives.
  if (state.itemId) return operation.conflictKey.toLowerCase() === state.conflictKey.toLowerCase();

  // Scalar redlines use the previous value as their unresolved current display.
  // If no action in this save touched the scalar target, do not turn that
  // presentation baseline into an implicit KeepPrevious decision.
  if (operation.kind !== "FieldSet") return false;
  if (operation.conflictKey.toLowerCase() !== state.conflictKey.toLowerCase()) return false;
  if (!state.baselineValueExists) return operation.value === undefined;
  return proposalSemanticJsonValuesEqual(operation.value, state.baselineValue);
}

/**
 * Fields the redline editor must never supply, because they identify the proposal or carry reviewer
 * state/identity the actual editor owns — never a derived revision that may have reconstructed
 * metadata. Kept as an explicit skip list rather than growing the schema-driven loop below with
 * special cases.
 */
const PROPOSAL_COLLABORATION_SNAPSHOT_SKIPPED_KEYS: ReadonlySet<string> = new Set([
  "id",
  "clientReviewers",
  "vendorReviewers",
]);

/**
 * Converts the active redline editor into the same flat JSON shape used by canonical operations.
 *
 * Schema-driven over `ProposalSchema.BaseProposalJSONZ`'s own keys (minus the skip list above) rather
 * than a hand-enumerated field list: a field added to that schema flows through this flatten
 * automatically, with no separate edit required here to avoid silently dropping it. Each key merges by
 * runtime shape — a defined plain object merges shallowly, preserving authoritative sub-keys the
 * redline left `undefined`; anything else (scalar, array, null) is taken from the redline wholesale
 * when defined, else falls back to the authoritative value.
 */
export function proposalCollaborationSnapshotFromRedline(proposal: Proposal): ProposalJSON {
  const authoritative = proposal.toJSON();
  const editedContent = proposal.redlinedRevision?.toJSON();
  if (!editedContent) return withoutLegacyUnresolvedFields(authoritative);

  const authoritativeRecord = authoritative as unknown as Record<string, unknown>;
  const editedRecord = editedContent as unknown as Record<string, unknown>;
  const merged: Record<string, unknown> = { ...authoritativeRecord };

  for (const key of Object.keys(ProposalSchema.BaseProposalJSONZ.shape)) {
    if (PROPOSAL_COLLABORATION_SNAPSHOT_SKIPPED_KEYS.has(key)) continue;
    merged[key] = mergeRedlineFieldValue(editedRecord[key], authoritativeRecord[key]);
  }

  return withoutLegacyUnresolvedFields(merged as ProposalJSON);
}

/**
 * A defined plain object merges shallowly — preserving authoritative keys a partial redline object
 * left `undefined` — while any other defined value (scalar, array, null) replaces the current one
 * wholesale. An `undefined` edited value always falls back to the current one, meaning "the redline
 * did not touch this field."
 */
function mergeRedlineFieldValue(edited: unknown, current: unknown): unknown {
  if (edited === undefined) return current;
  if (!isPlainRecord(edited) || !isPlainRecord(current)) return edited;

  const merged: Record<string, unknown> = { ...current };
  for (const [key, value] of Object.entries(edited)) {
    if (value !== undefined) merged[key] = value;
  }
  return merged;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function withoutLegacyUnresolvedFields(proposalJSON: ProposalJSON): ProposalJSON {
  const result = structuredClone(proposalJSON);
  stripLegacyUnresolvedFields(result);
  return result;
}

function stripLegacyUnresolvedFields(proposalJSON?: ProposalJSON | null): void {
  if (!proposalJSON) return;
  delete proposalJSON.unresolvedFields;
  stripLegacyUnresolvedFields(proposalJSON.partyRevision);
  stripLegacyUnresolvedFields(proposalJSON.supersedes);
}
